/**
 * V1.9.5 — Code Violations dialog (Studio Tier preview / free advisory).
 *
 * Calls GET /api/designs/:id/code-violations and renders a sorted list
 * of residential code-compliance findings grouped by severity. Inspired
 * by the lint-results panel in modern IDEs.
 *
 * The dialog is non-blocking and read-only — it's a pre-export sanity
 * check, not a hard gate. Future enhancement: a "Fix critical issues"
 * button that triggers an auto-repair chat turn.
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { ShieldCheck, AlertOctagon, AlertTriangle, Info, RefreshCw, Wrench } from "lucide-react";
import { getCodeViolations } from "../lib/api";

const SEVERITY_META = {
  critical: { icon: AlertOctagon, color: "#ef4444", label: "CRITICAL", bg: "rgba(239, 68, 68, 0.08)" },
  warning:  { icon: AlertTriangle, color: "#f59e0b", label: "WARNING",  bg: "rgba(245, 158, 11, 0.08)" },
  advisory: { icon: Info,          color: "#c4a96b", label: "ADVISORY", bg: "rgba(196, 169, 107, 0.10)" },
};

export default function CodeViolationsDialog({ open, onClose, designId, onAutoFix }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runScan = async () => {
    if (!designId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await getCodeViolations(designId);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not run code-compliance scan.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) runScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, designId]);

  const summary = data?.summary;
  const violations = data?.violations || [];
  const isClean = summary && summary.total === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-3xl bg-zinc-950 border-zinc-800 text-zinc-100"
        data-testid="code-violations-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-mono text-brass-400 flex items-center gap-2">
            <ShieldCheck size={18} />
            // CODE COMPLIANCE SCAN
          </DialogTitle>
        </DialogHeader>

        {/* Summary chips */}
        <div className="flex items-center justify-between gap-3 border-b border-zinc-800 pb-3">
          <div className="flex flex-wrap gap-2 text-xs font-mono">
            {summary && (
              <>
                <span
                  data-testid="cv-summary-critical"
                  className="px-2 py-1 rounded"
                  style={{ background: SEVERITY_META.critical.bg, color: SEVERITY_META.critical.color }}
                >
                  CRITICAL · {summary.critical}
                </span>
                <span
                  data-testid="cv-summary-warning"
                  className="px-2 py-1 rounded"
                  style={{ background: SEVERITY_META.warning.bg, color: SEVERITY_META.warning.color }}
                >
                  WARNING · {summary.warning}
                </span>
                <span
                  data-testid="cv-summary-advisory"
                  className="px-2 py-1 rounded"
                  style={{ background: SEVERITY_META.advisory.bg, color: SEVERITY_META.advisory.color }}
                >
                  ADVISORY · {summary.advisory}
                </span>
              </>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-brass-500/40 text-brass-300 hover:bg-brass-500/10 font-mono text-xs"
            onClick={runScan}
            disabled={loading}
            data-testid="cv-rescan-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin mr-2" : "mr-2"} />
            {loading ? "Scanning..." : "Re-scan"}
          </Button>
        </div>

        {/* Body */}
        <div
          className="max-h-[55vh] overflow-y-auto pr-1"
          data-testid="cv-results"
        >
          {loading && !data && (
            <div className="py-8 text-center text-zinc-500 font-mono text-xs">
              // analysing blueprint against IRC 2021 + Architectural Graphic Standards...
            </div>
          )}
          {error && (
            <div className="py-4 text-red-400 font-mono text-xs">
              // ERROR: {error}
            </div>
          )}
          {!loading && !error && isClean && (
            <div
              data-testid="cv-clean"
              className="py-10 text-center font-mono text-sm text-emerald-400 flex flex-col items-center gap-3"
            >
              <ShieldCheck size={44} className="opacity-70" />
              <div>// NO VIOLATIONS DETECTED</div>
              <div className="text-zinc-500 text-xs max-w-md">
                This design passes every rule in the current residential code-compliance ruleset.
                Note: this is an advisory scan, not a permit-grade review.
              </div>
            </div>
          )}
          {!loading && violations.length > 0 && (
            <ul className="divide-y divide-zinc-800">
              {violations.map((v) => {
                const meta = SEVERITY_META[v.severity] || SEVERITY_META.advisory;
                const Icon = meta.icon;
                return (
                  <li
                    key={v.id}
                    data-testid={`cv-row-${v.id}`}
                    className="py-3 flex gap-3 items-start"
                  >
                    <Icon size={18} style={{ color: meta.color, flexShrink: 0, marginTop: 2 }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="font-mono text-xs" style={{ color: meta.color }}>
                          {meta.label}
                        </span>
                        <span className="font-mono text-[10px] text-zinc-500">
                          {v.rule_code} · {v.category}
                        </span>
                      </div>
                      <div className="text-sm text-zinc-100 mb-1">{v.title}</div>
                      <div className="text-xs text-zinc-400 mb-1">{v.detail}</div>
                      <div className="flex items-center justify-between gap-3 mt-1">
                        <div className="text-xs text-brass-300 font-mono flex-1">
                          // FIX: {v.remedy}
                        </div>
                        {onAutoFix && (
                          <button
                            type="button"
                            data-testid={`cv-autofix-${v.id}`}
                            onClick={() => onAutoFix(v)}
                            title="Ask the AI architect to repair this violation"
                            className="flex-shrink-0 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest border border-brass-500/40 text-brass-200 hover:bg-brass-500/10 px-2 py-1 transition-colors"
                          >
                            <Wrench size={11} />
                            Auto-Fix
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-zinc-800 text-[10px] font-mono text-zinc-500">
          <span>// Advisory scan — not a substitute for a stamped permit review.</span>
          <span>IRC 2021 · AIA Graphic Standards</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
